import React from "react";

/**
 * La carte de mutuelle, telle que la secrétaire en a besoin.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE QU'IL Y AVAIT AVANT, ET POURQUOI C'ÉTAIT ILLISIBLE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Nicolas, 18/09 : « Pour la mutuelle c'est aussi très moche. […] Là tes
 * champs sont incompréhensibles », capture à l'appui. On y lisait
 * « reseauTiersPayant », « dateDebutValidite », « lecture : [object Object] »,
 * « beneficiaires : [object Object],[object Object] », « aSignaler : false »,
 * et deux fois le même prénom sous deux noms.
 *
 * Ce n'était pas un défaut de présentation, c'était l'absence de présentation :
 * l'écran faisait `Object.entries(mutuelleResume).map(...)` et affichait les
 * clés du JSON telles quelles, valeur passée à `String()` — d'où les
 * « [object Object] ». Le serveur, de son côté, fusionnait la réponse brute du
 * modèle avec le résumé mis au propre (corrigé le même jour dans
 * `card_ocr_controller`), ce qui expliquait les doublons français/anglais.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE QU'IL A DEMANDÉ, ET COMMENT C'EST RENDU
 * ─────────────────────────────────────────────────────────────────────────
 *
 * « Présente directement le numéro d'AMC et le numéro d'adhérent en
 * clic/copie. Mets un liseré ou une petite icone si la mutuelle est toujours
 * en période de validité. pour le reste on peut en effet déplier les infos et
 * présente les comme sur les carte de mutuelle avec notamment la liste des
 * personnes assurées et vérifie que la prise en charge Laboratoire est bien à
 * 100%, récupère ce chiffre et affiche le. »
 *
 *   — les DEUX numéros sont en tête, en gros, et un clic les copie : ce sont
 *     les deux seules choses qu'elle recopie vraiment dans le SIL ;
 *   — un liseré coloré sur toute la hauteur dit la validité d'un coup d'œil,
 *     avec l'icône et la phrase que calcule déjà `mutuelleService` ;
 *   — le taux de biologie est affiché quand la carte le porte, et signalé
 *     seulement s'il n'est pas de 100 % ;
 *   — le reste se déplie, rangé comme sur une carte, la liste des assurés
 *     comprise.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 01/10 : LA CARTE CHANGE DE TAILLE, DE COULEUR, ET SE FAIT LIRE ICI
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le client, capture à l'appui : la carte était la seule zone de l'écran à
 * grossir ses valeurs. Elle rentre maintenant dans l'échelle du dossier
 * (valeurs 12px comme `.sb-v`, libellés 10px comme `.sb-k`, poids 600 au
 * plus) — c'est une carte de LECTURE, pas un pavé de saisie.
 *
 * Tout ce qu'elle montre vient d'une reconnaissance automatique : ses
 * pastilles passent donc au violet déjà employé partout ailleurs dans le
 * produit pour « lu par une machine », et la carte l'annonce d'entrée. Les
 * pastilles d'ALERTE gardent leur ambre : un avertissement n'est pas une
 * provenance, deux informations différentes ne portent pas la même couleur.
 *
 * Et c'est le BACK-OFFICE qui fait lire la carte (voir `Utils/lectureMutuelle`
 * et l'effet de `SecretaryBackoffice`) : sans résumé, la carte ne rend plus
 * « rien » — elle dit où en est la lecture, ou que les photos de la tablette
 * ne sont pas encore arrivées. Un vide se lit comme « pas de mutuelle ».
 *
 * La carte, elle, ne remplit RIEN dans l'identité du patient : elle se
 * contente d'afficher. C'est `Utils/choixIdentite` qui décide ce que l'écran
 * des droits propose comme sources, et la mutuelle y figure déjà comme
 * candidate — une instruction de Nicolas du 18/09, couverte par ses tests.
 * La lecture automatique ne l'y ajoute pas : elle remplit `mutuelleResume`
 * sur le rendez-vous, rien d'autre.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 01/10 AU SOIR : LA CARTE SE REPLIE, ET LE VIOLET REMONTE DANS L'IDENTITÉ
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le client, deux remarques sur la même revue :
 *
 *   — « la taille est toujours trop grande, regarde la phrase sur la biologie
 *     prise à 100 % ». La veille on était descendu à 12px, ça ne suffisait
 *     pas : ce n'est pas une carte qu'on lit en entier, c'est une carte qu'on
 *     CONSULTE. Tout passe donc à 11px (libellés 10px), poids 500 au plus,
 *     et la ligne de biologie devient une ligne discrète au lieu d'une
 *     vedette ;
 *   — le violet n'était pas au bon endroit. Il est la marque de la lecture
 *     automatique, et le client le veut EN HAUT, sur les quelques valeurs
 *     qu'il recopie dans le SIL (voir `ChipsMutuelle` dans l'écran). La
 *     carte, elle, redevient neutre : ses cases et ses pastilles reprennent
 *     le gris-bleu du dossier, et seul « Lu automatiquement » reste violet —
 *     c'est la seule chose qui ne se déduit pas de la position.
 *
 * Et comme l'essentiel est désormais là-haut, la carte est REPLIÉE par
 * défaut : on la déplie quand on veut le détail, pas pour travailler.
 */

/** Les quatre états de validité, et ce qu'ils valent à l'œil. */
const TONS = {
  // `statutReplie` est ce qu'on voit quand la carte est fermee : rien du tout
  // pour une mutuelle valable — un rond vert plein se lit sans glyphe — et un
  // point d'exclamation des qu'il y a quelque chose a regarder.
  valide: { bord: "#10B981", fond: "#e9fbf2", texte: "#0b6f3b", icone: "✓", statutReplie: "" },
  bientot_expiree: { bord: "#F59E0B", fond: "#fff8e6", texte: "#8a5b00", icone: "!", statutReplie: "!" },
  expiree: { bord: "#EF4444", fond: "#fff1f2", texte: "#9f1239", icone: "✕", statutReplie: "!" },
  pas_encore_valide: { bord: "#EF4444", fond: "#fff1f2", texte: "#9f1239", icone: "✕", statutReplie: "!" },
  inconnue: { bord: "#CBD5E1", fond: "#f8fafc", texte: "#475569", icone: "?", statutReplie: "?" },
};

function Numero({ libelle, valeur, provenance, onCopier }) {
  if (!valeur) return null;
  return (
    <button type="button" className="sb-mutNum" onClick={() => onCopier(valeur)}>
      <span className="sb-k">
        {libelle}
        {provenance === "datamatrix" && <span className="sb-src nir">Data Matrix</span>}
      </span>
      <span className="sb-mutNumValeur">{valeur}</span>
    </button>
  );
}

function Ligne({ libelle, valeur, onCopier }) {
  if (!valeur) return null;
  return (
    <button type="button" className="sb-copyBtn" onClick={() => onCopier(String(valeur))}>
      <div className="sb-k">{libelle}</div>
      <div className="sb-v"><span>{valeur}</span></div>
    </button>
  );
}

/**
 * Ce qu'on dit quand il n'y a PAS encore de resume a montrer.
 *
 * Depuis le 01/10, c'est le back-office qui fait lire la carte : pendant les
 * quelques secondes de la lecture, la carte disait « rien » — ce qui se lit
 * exactement comme « pas de mutuelle ». Deux cas, deux phrases :
 *
 *   — `etat === "lecture"` : la lecture est en cours ;
 *   — `etat === "illisible"` : le serveur a essaye et n'y est pas arrive ;
 *     un lien « Relire » le refait, la secretaire a la carte sous les yeux.
 */
function EtatLecture({ etat, onRelire }) {
  if (etat === "lecture") {
    return <div className="sb-mutLecture">Lecture de la carte de mutuelle…</div>;
  }
  if (etat === "illisible") {
    return (
      <div className="sb-mutLecture">
        <span>Carte de mutuelle illisible</span>
        <button type="button" className="sb-btnMini2" onClick={onRelire}>Relire</button>
      </div>
    );
  }
  return null;
}

export default function CarteMutuelle({ resume, ouvert, onBasculer, onCopier, etat = null, onRelire }) {
  /*
    Sans resume ET sans etat de lecture, la carte ne rend rien : c'est un
    dossier qui n'a pas de mutuelle, et une carte vide se lirait comme une
    mutuelle dont on n'aurait rien lu.
  */
  if (!resume) return etat ? (
    <div className="sb-card sb-mutCard">
      <h3><span className="sb-icon">&#x1F4B3;</span> Mutuelle</h3>
      <EtatLecture etat={etat} onRelire={onRelire} />
    </div>
  ) : null;

  const validite = resume.validite || {};
  const ton = TONS[validite.statut] || TONS.inconnue;
  const assures = Array.isArray(resume.beneficiaires) ? resume.beneficiaires : [];

  return (
    <div className="sb-card sb-mutCard" style={{ borderLeft: `4px solid ${ton.bord}` }}>
      <h3>
        <span className="sb-icon">&#x1F4B3;</span> Mutuelle
        {/*
          « LU AUTOMATIQUEMENT » EN TETE DE CARTE.

          01/10, le client : tout ce qui vient d'une reconnaissance automatique
          est en violet, et la carte de mutuelle doit le dire d'entree. La
          pastille est la meme que partout ailleurs dans le produit
          (`sb-src nir`) : une seule couleur pour « lu par une machine », et
          pas une couleur par type de lecture.

          C'est la SEULE chose violettte qui reste dans la carte depuis le
          01/10 au soir : le client voulait le violet ailleurs (sur les chips
          de l'identite, la ou il recopie), et la carte reprend les couleurs
          neutres du dossier. Cette etiquette, elle, ne se deduit pas de la
          position : c'est elle qui dit d'ou vient ce qu'on lit plus bas.
        */}
        <span className="sb-src nir">Lu automatiquement</span>
        {resume.nomMutuelle ? (
          <span className="sb-mutNom">{resume.nomMutuelle}</span>
        ) : null}
        {/*
          REPLIÉ, LA VALIDITÉ TIENT DANS UNE PASTILLE.

          Nicolas, 18/09 (18h18) : « Pour gagner de la place quand c'est
          replié, mets juste le petit rond Vert à côté du nom de la mutuelle
          quand la validité est OK et affiche tout comme actuellement quand on
          déplie. Quand la mutuelle n'est pas valide, un petit logo Attention
          en Jaune ou rouge serait le bienvenu. »

          La phrase complète reste en infobulle : elle n'occupe plus de ligne,
          mais elle n'est pas perdue.
        */}
        {!ouvert && (
          <span
            className="sb-mutRond"
            style={{ background: ton.bord }}
            title={validite.message || "Validité inconnue."}
            aria-label={validite.message || "Validité inconnue."}
          >
            {ton.statutReplie}
          </span>
        )}
        <button type="button" className="sb-btnMini2" style={{ marginLeft: "auto" }} onClick={onBasculer}>
          {ouvert ? "Replier" : "Déplier"}
        </button>
      </h3>

      {/* La validité en toutes lettres : seulement quand c'est déplié. */}
      {ouvert && (
        <div
          className="sb-mutValidite"
          style={{ background: ton.fond, color: ton.texte, borderColor: ton.bord }}
        >
          <span className="sb-mutPastille" style={{ background: ton.bord }}>{ton.icone}</span>
          <span>{validite.message || "Validité inconnue."}</span>
        </div>
      )}

      {/* Les deux numéros qu'on recopie dans le SIL, en clic/copie. */}
      <div className="sb-mutNums">
        <Numero
          libelle="N° AMC"
          valeur={resume.numeroAMC}
          provenance={resume.provenanceNumeros?.numeroAMC}
          onCopier={onCopier}
        />
        <Numero
          libelle="N° adhérent"
          valeur={resume.numeroAdherent}
          provenance={resume.provenanceNumeros?.numeroAdherent}
          onCopier={onCopier}
        />
      </div>

      {/* Le taux de biologie : affiché quand il est lu, signalé s'il n'est
          pas entier. */}
      {/*
        LE TAUX BIOLOGIE SE DIT MEME QUAND IL EST ABSENT.

        Nicolas, 18/09 (18h18) : « je ne vois pas le taux de prise en charge
        Labo sur les infos recuperees. »

        La ligne ne s'affichait que si le taux avait ete lu — donc, sur une
        carte qui n'imprime aucun tableau de garanties, elle n'existait pas du
        tout, et rien ne distinguait « la carte ne le dit pas » de « on a
        oublie de le chercher ». On affiche donc la ligne dans les deux cas :
        le chiffre quand il y est, « non indique sur la carte » sinon. C'est
        la meme regle que pour les numeros manquants, et pour la meme raison :
        un champ vide sans explication se lit comme un bogue.

        01/10 au soir : « regarde la phrase sur la biologie prise a 100 % ».
        C'est cette ligne-la qui prenait toute la place. Elle passe a 11px,
        comme le reste de la carte, et ne s'ecrit plus qu'en une seule phrase
        — « Biologie prise en charge 100 % » — au lieu d'un libelle et d'une
        valeur qui se repondent a travers la ligne.
      */}
      <div className="sb-mutTaux">
        {resume.priseEnChargeLaboratoire ? (
          <span className={`sb-mutTauxValeur${resume.alertePriseEnCharge ? " sb-mutTauxPartiel" : ""}`}>
            Biologie prise en charge {resume.priseEnChargeLaboratoire}
          </span>
        ) : (
          <span className="sb-mutTauxAbsent">Biologie prise en charge : non indiqué sur la carte</span>
        )}
      </div>
      {resume.alertePriseEnCharge ? (
        <div className="sb-pill sb-nv">{resume.alertePriseEnCharge}</div>
      ) : null}

      {resume.informationsManquantes?.length ? (
        <div className="sb-pill sb-nv">
          Non lu sur la carte : {resume.informationsManquantes.join(", ")}.
        </div>
      ) : null}

      {ouvert ? (
        <>
          <div className="sb-grid3" style={{ marginTop: 8 }}>
            <Ligne libelle="Organisme" valeur={resume.nomMutuelle} onCopier={onCopier} />
            <Ligne libelle="Réseau tiers payant" valeur={resume.reseauTiersPayant} onCopier={onCopier} />
            <Ligne libelle="N° préfectoral" valeur={resume.numeroPrefectoral} onCopier={onCopier} />
            <Ligne libelle="N° de contrat" valeur={resume.numeroContrat} onCopier={onCopier} />
            <Ligne libelle="Valable du" valeur={resume.dateDebutValidite} onCopier={onCopier} />
            <Ligne libelle="Valable jusqu'au" valeur={resume.dateFinValidite} onCopier={onCopier} />
          </div>

          {/* Les personnes assurées, comme elles figurent sur la carte. */}
          {/*
            LE TITRE SUIT L'ECHELLE DE LA CARTE, LUI AUSSI.

            Il portait `.sb-label`, qui est a 12px en 800 la ou la carte est
            desormais a 11px en 500 : un titre plus gras que ce qu'il annonce
            attire l'oeil sur la seule ligne qui n'apprend rien de neuf.
          */}
          <div className="sb-k" style={{ marginTop: 10, marginBottom: 4 }}>
            Personnes assurées{assures.length ? ` (${assures.length})` : ""}
          </div>
          {assures.length ? (
            <div className="sb-mutAssures">
              {assures.map((a, i) => (
                <button
                  key={`${a.nom}-${a.prenom}-${i}`}
                  type="button"
                  className="sb-mutAssure"
                  onClick={() => onCopier(`${a.nom} ${a.prenom}`.trim())}
                >
                  <span className="sb-mutAssureNom">
                    {a.nom} {a.prenom}
                  </span>
                  {a.dateNaissance ? (
                    <span className="sb-mutAssureDdn">{a.dateNaissance}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : (
            <div className="sb-pill">
              La carte ne liste qu'un bénéficiaire, ou la liste n'a pas été lue.
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
